import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ChevronRight, Download, Loader2, Ticket, X } from 'lucide-react';
import { useGaps, useUnlinkGaps, type GapGroup, type GapItem, type GapKind, type GapSeverity } from '@/api/insights';
import { PageHeader } from '@/components/PageHeader';
import { StatCard } from '@/components/dashboard/StatCard';
import { DesktopOnly } from '@/components/dashboard/DesktopOnly';
import { SearchInput } from '@/components/ui/search-input';
import { Button } from '@/components/ui/button';
import { GapTicketDialog } from '@/components/insights/GapTicketDialog';
import { ROUTES, backupSaasCustomerPath, companyPath, insightsCoverageView, rmmDevicePath, ticketPath } from '@/lib/routes';
import { cn } from '@/lib/utils';

/**
 * Insights → Gap list. Every actionable coverage gap across modules, one remediation sentence per
 * client × kind, ranked worst-first (server/insights/gaps.ts). Each group or item can be linked to
 * an open Autotask ticket or get a new one; the ticket then shows here until the gap clears.
 */

type Show = 'open' | 'all';
type SevFilter = GapSeverity | 'any';

const KINDS: GapKind[] = ['no-rmm', 'no-edr', 'no-backup', 'no-intune', 'drift', 'edr-silent', 'not-billed', 'leaver', 'duplicate'];
/** Short chip labels so the kind filter stays on one row; the server's full label is the tooltip. */
const KIND_CHIP: Record<GapKind, string> = {
  'no-rmm': 'No RMM agent', 'no-edr': 'No Huntress', 'no-backup': 'Not backed up', 'no-intune': 'Not in Intune',
  drift: 'Check-in drift', 'edr-silent': 'Huntress silent', 'not-billed': 'Not billed', leaver: 'Leavers', duplicate: 'Duplicates',
};
const SEV_CHIP: Record<GapSeverity, { label: string; cls: string }> = {
  critical: { label: 'Critical', cls: 'bg-destructive/10 text-destructive' },
  warning:  { label: 'Warning',  cls: 'bg-warning-soft text-warning' },
  advisory: { label: 'Advisory', cls: 'bg-muted text-muted-foreground' },
};
/** Where each kind is worked on in its own module. */
const KIND_SOURCE: Partial<Record<GapKind, { label: string; to: string }>> = {
  'no-rmm':    { label: 'Device coverage', to: insightsCoverageView({ state: 'intune-only' }) },
  'no-intune': { label: 'Device coverage', to: insightsCoverageView({ state: 'rmm-only' }) },
  drift:       { label: 'Device coverage', to: insightsCoverageView({ state: 'drift' }) },
  duplicate:   { label: 'Device coverage', to: insightsCoverageView({ state: 'duplicates' }) },
  'not-billed': { label: 'RMM sites', to: ROUTES.rmm.sites },
};

const PREF = 'cosmos:insights:gaps';
/** An item still needs someone: no ticket, or its ticket is complete but the gap is still there. */
const needsAction = (i: GapItem) => !i.ticket || i.ticket.complete;

export default function InsightsGapsPage() {
  const { data, isLoading, error, isFetching } = useGaps({ scoped: true });
  const [params, setParams] = useSearchParams();
  const saved = (() => { try { return JSON.parse(localStorage.getItem(PREF) ?? '{}') as { show?: Show; kind?: GapKind | 'all' }; } catch { return {}; } })();
  const [show, setShowState] = useState<Show>(saved.show ?? 'open');
  const [kind, setKindState] = useState<GapKind | 'all'>(saved.kind ?? 'all');
  const [sev, setSev] = useState<SevFilter>('any');
  const [q, setQ] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [dialog, setDialog] = useState<{ group: GapGroup; items: GapItem[] } | null>(null);
  const persist = (p: { show?: Show; kind?: GapKind | 'all' }) => { try { localStorage.setItem(PREF, JSON.stringify({ show, kind, ...p })); } catch { /* storage unavailable */ } };
  const setShow = (s: Show) => { setShowState(s); persist({ show: s }); };
  const setKind = (k: GapKind | 'all') => { setKindState(k); persist({ kind: k }); };

  // Menu views (?kind=…&severity=…, Orbit's ?q=) replace the saved filter, then leave the URL.
  useEffect(() => {
    if (!params.has('kind') && !params.has('severity') && !params.has('q')) return;
    const k = params.get('kind') as GapKind | null;
    setKind(k && KINDS.includes(k) ? k : 'all');
    const s = params.get('severity') as GapSeverity | null;
    setSev(s && s in SEV_CHIP ? s : 'any');
    setQ(params.get('q') ?? '');
    setParams({}, { replace: true });
  }, [params, setParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Groups narrowed to the items that pass "show"; empty groups drop out.
  const visible = useMemo(() => (data?.groups ?? [])
    .map((g) => ({ ...g, items: show === 'open' ? g.items.filter(needsAction) : g.items }))
    .filter((g) => g.items.length > 0), [data, show]);

  const kindCounts = useMemo(() => {
    const m = Object.fromEntries(KINDS.map((k) => [k, 0])) as Record<GapKind, number>;
    for (const g of visible) if (sev === 'any' || g.severity === sev) m[g.kind] += g.items.length;
    return m;
  }, [visible, sev]);

  const groups = useMemo(() => {
    const s = q.trim().toLowerCase();
    return visible
      .filter((g) => (kind === 'all' || g.kind === kind) && (sev === 'any' || g.severity === sev))
      .map((g) => (!s || (g.companyName ?? '').toLowerCase().includes(s) || g.title.toLowerCase().includes(s)) ? g
        : { ...g, items: g.items.filter((i) => i.subject.toLowerCase().includes(s) || i.detail.toLowerCase().includes(s)) })
      .filter((g) => g.items.length > 0);
  }, [visible, kind, sev, q]);

  const all = data?.groups.flatMap((g) => g.items.map((i) => ({ g, i }))) ?? [];
  const sevCount = (s: GapSeverity) => all.filter(({ g, i }) => g.severity === s && needsAction(i)).length;
  const ticketed = all.filter(({ i }) => i.ticket && !i.ticket.complete).length;
  const reopened = all.filter(({ i }) => i.ticket?.complete).length;
  const itemCount = groups.reduce((n, g) => n + g.items.length, 0);

  const toggle = (key: string) => setExpanded((prev) => { const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n; });

  return (
    <DesktopOnly title="Insights are available on desktop" message="Open Cosmos on a larger screen to view the gap list.">
      <div className="mx-auto w-full max-w-[1400px] px-4 py-5 md:px-6">
        <PageHeader title="Gap list" count={data ? itemCount : null} isFetching={isFetching}
          subtitle="Every actionable coverage gap across Datto RMM, Intune and SaaS backup, grouped by client and ranked worst-first"
          actions={
            <div className="flex items-center gap-2">
              <SearchInput withIcon placeholder="Client, device or user" className="w-64" value={q} onValueChange={setQ} />
              <Button variant="outline" size="sm" asChild>
                <a href="/api/insights/gaps?format=csv" download><Download className="mr-1.5 h-3.5 w-3.5" />CSV</a>
              </Button>
            </div>
          } />

        {isLoading ? (
          <div className="flex items-center gap-2 py-16 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Collecting gaps from every module…</div>
        ) : error ? (
          <div className="mt-4 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">Failed to load: {error instanceof Error ? error.message : 'Unknown error'}</div>
        ) : data ? (
          <>
            {(!data.sources.intune || !data.sources.backup) && (
              <div className="mt-3 rounded-md border border-warning/30 bg-warning-soft px-3 py-2 text-xs text-warning">
                {!data.sources.intune && 'Intune data isn\'t loaded yet, so device gaps are incomplete. '}
                {!data.sources.backup && 'SaaS backup data isn\'t available, so backup gaps aren\'t shown.'}
              </div>
            )}

            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
              <StatCard label="Critical" value={sevCount('critical')} sub="no RMM / Huntress agent · not backed up" tone={sevCount('critical') ? 'destructive' : 'success'} onClick={() => { setSev('critical'); setKind('all'); setShow('open'); }} />
              <StatCard label="Warning" value={sevCount('warning')} sub="not in Intune · not checking in" tone={sevCount('warning') ? 'warning' : 'success'} onClick={() => { setSev('warning'); setKind('all'); setShow('open'); }} />
              <StatCard label="Advisory" value={sevCount('advisory')} sub="leavers · duplicate records" onClick={() => { setSev('advisory'); setKind('all'); setShow('open'); }} />
              <StatCard label="Ticketed" value={ticketed} sub="linked to an open ticket" onClick={() => { setSev('any'); setKind('all'); setShow('all'); }} />
              <StatCard label="Ticket closed, gap remains" value={reopened} sub="needs another look" tone={reopened ? 'warning' : 'default'} onClick={() => { setSev('any'); setKind('all'); setShow('open'); }} />
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2">
              <div className="flex gap-1 rounded-lg bg-muted p-1">
                {(['open', 'all'] as Show[]).map((s) => (
                  <button key={s} type="button" onClick={() => setShow(s)}
                    className={cn('rounded-md px-3 py-1 text-xs font-medium', show === s ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground')}>
                    {s === 'open' ? 'Needs action' : 'Include ticketed'}
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap gap-1 rounded-lg bg-muted p-1">
                <button type="button" onClick={() => setKind('all')}
                  className={cn('rounded-md px-3 py-1 text-xs font-medium', kind === 'all' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground')}>All kinds</button>
                {KINDS.filter((k) => kindCounts[k] > 0 || kind === k).map((k) => (
                  <button key={k} type="button" onClick={() => setKind(k)} title={data.kindLabels[k]}
                    className={cn('rounded-md px-3 py-1 text-xs font-medium', kind === k ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground')}>
                    {KIND_CHIP[k]} <span className="ml-1 tabular-nums text-muted-foreground">{kindCounts[k]}</span>
                  </button>
                ))}
              </div>
              {sev !== 'any' && (
                <button type="button" onClick={() => setSev('any')} className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold', SEV_CHIP[sev].cls)}>
                  {SEV_CHIP[sev].label} only <X className="h-3 w-3" />
                </button>
              )}
            </div>

            <div className="mt-3 overflow-hidden rounded-lg border border-border bg-card shadow-sm">
              {groups.length === 0 && <div className="px-3 py-12 text-center text-sm text-muted-foreground">{show === 'open' ? 'Nothing needs action here.' : 'No gaps match.'}</div>}
              {groups.map((g) => (
                <GroupRow key={g.key} group={g} open={expanded.has(g.key) || !!q.trim()} onToggle={() => toggle(g.key)}
                  onTicket={(items) => setDialog({ group: g, items })} />
              ))}
            </div>
            <p className="mt-2 text-[11px] text-muted-foreground">
              Device gaps follow <Link to={ROUTES.insights.coverage} className="hover:underline">Device coverage</Link> (only where the client really uses both tools; stale records ignored
              per <Link to={ROUTES.admin.insights} className="hover:underline">Admin → Insights</Link>). Backup gaps compare SaaS Protection seats with licensed M365 members.
              Records not linked to any client are under <Link to={`${ROUTES.companies}?show=unmapped`} className="hover:underline">Mapping health</Link>.
            </p>
          </>
        ) : null}

        {dialog && <GapTicketDialog open onOpenChange={(o) => { if (!o) setDialog(null); }} group={dialog.group} items={dialog.items} />}
      </div>
    </DesktopOnly>
  );
}

function GroupRow({ group: g, open, onToggle, onTicket }: { group: GapGroup; open: boolean; onToggle: () => void; onTicket: (items: GapItem[]) => void }) {
  const untracked = g.items.filter(needsAction);
  const tickets = [...new Map(g.items.filter((i) => i.ticket).map((i) => [i.ticket!.id, i.ticket!])).values()];
  const source = g.saasCustomerId != null ? { label: 'Backup customer', to: backupSaasCustomerPath(g.saasCustomerId) } : KIND_SOURCE[g.kind];
  return (
    <div className="border-b border-divider last:border-b-0">
      <div className="flex items-center gap-3 px-3 py-2.5 hover:bg-muted/30">
        <button type="button" onClick={onToggle} className="flex min-w-0 flex-1 items-center gap-3 text-left" aria-expanded={open}>
          <ChevronRight className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform', open && 'rotate-90')} />
          <span className={cn('w-20 shrink-0 rounded-full px-2 py-0.5 text-center text-[11px] font-semibold', SEV_CHIP[g.severity].cls)}>{SEV_CHIP[g.severity].label}</span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium text-foreground" title={g.title}>{g.title}</span>
            <span className="block truncate text-[11px] text-muted-foreground">
              {g.items.length} item{g.items.length === 1 ? '' : 's'} · next step: {g.action}
            </span>
          </span>
        </button>
        <div className="flex shrink-0 items-center gap-2 text-xs">
          {g.companyId != null
            ? <Link to={companyPath(g.companyId)} className="max-w-[180px] truncate text-muted-foreground hover:text-foreground hover:underline" title={g.companyName ?? ''}>{g.companyName}</Link>
            : <span className="italic text-muted-foreground">not mapped</span>}
          {tickets.slice(0, 2).map((t) => <TicketChip key={t.id} ticket={t} />)}
          {tickets.length > 2 && <span className="text-muted-foreground">+{tickets.length - 2}</span>}
          {untracked.length > 0 && (
            <Button size="sm" variant="outline" className="h-7" onClick={() => onTicket(untracked)}>
              <Ticket className="mr-1 h-3.5 w-3.5" />Ticket{untracked.length > 1 ? ` (${untracked.length})` : ''}
            </Button>
          )}
        </div>
      </div>
      {open && (
        <div className="border-t border-divider bg-muted/20 px-3 pb-2 pl-10">
          {source && <div className="pt-2 text-[11px] text-muted-foreground">Work on it in <Link to={source.to} className="hover:text-foreground hover:underline">{source.label}</Link></div>}
          <table className="mt-1 w-full text-xs" style={{ tableLayout: 'fixed' }}>
            <colgroup><col style={{ width: 260 }} /><col /><col style={{ width: 230 }} /><col style={{ width: 90 }} /></colgroup>
            <tbody className="divide-y divide-divider">
              {g.items.map((i) => (
                <tr key={i.key} className="align-top">
                  <td className="overflow-hidden py-1.5 pr-3">
                    {i.rmmUid ? <Link to={rmmDevicePath(i.rmmUid)} className="block truncate font-medium text-foreground hover:underline" title={i.subject}>{i.subject}</Link>
                      : <span className="block truncate font-medium text-foreground" title={i.subject}>{i.subject}</span>}
                  </td>
                  <td className="overflow-hidden py-1.5 pr-3 text-muted-foreground"><span className="block truncate" title={i.detail}>{i.detail}</span></td>
                  <td className="py-1.5 pr-3">{i.ticket ? <ItemTicket item={i} /> : <span className="text-muted-foreground">No ticket</span>}</td>
                  <td className="py-1.5 text-right">
                    {needsAction(i) && <button type="button" onClick={() => onTicket([i])} className="text-primary hover:underline">Ticket…</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function TicketChip({ ticket: t }: { ticket: NonNullable<GapItem['ticket']> }) {
  return (
    <Link to={ticketPath(t.id)} title={`${t.title ?? ''}${t.statusLabel ? ` (${t.statusLabel})` : ''} · linked by ${t.linkedBy}`}
      className={cn('rounded-full px-2 py-0.5 font-mono text-[11px] hover:underline', t.complete ? 'bg-warning-soft text-warning' : 'bg-primary/10 text-primary')}>
      {t.ticketNumber}
    </Link>
  );
}

function ItemTicket({ item }: { item: GapItem }) {
  const unlink = useUnlinkGaps();
  const t = item.ticket!;
  return (
    <span className="inline-flex max-w-full items-center gap-1.5">
      <TicketChip ticket={t} />
      <span className={cn('truncate', t.complete ? 'text-warning' : 'text-muted-foreground')}>
        {t.complete ? 'Complete, gap remains' : t.statusLabel ?? ''}
      </span>
      <button type="button" title="Unlink this ticket" disabled={unlink.isPending} className="text-muted-foreground hover:text-foreground"
        onClick={() => unlink.mutate([item.key], { onSuccess: () => toast.success(`Unlinked ${t.ticketNumber}`), onError: (e) => toast.error('Failed to unlink', { description: e instanceof Error ? e.message : '' }) })}>
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}
